import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Heart } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  useSEO({ title: "Thank You — The Tandem T Foundation", path: "/payment/success" });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (data.payment_status === "failed" || data.payment_status === "expired") { setState("error"); return; }
      if (attempt >= MAX_ATTEMPTS) { setState("pending"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      if (attempt >= MAX_ATTEMPTS) { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div data-testid="payment-success" className="min-h-[80vh] flex items-center justify-center px-6 pt-28 pb-20">
      <div className="max-w-xl w-full text-center">
        {state === "checking" && (
          <div data-testid="pay-checking">
            <Loader2 size={40} className="mx-auto animate-spin text-white/70" />
            <p className="font-serif text-3xl font-light text-white mt-8">Confirming your gift…</p>
            <p className="text-white/55 mt-3">One moment while we verify your donation.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="pay-paid">
            <CheckCircle2 size={48} className="mx-auto text-white" />
            <p className="overline text-white/55 mt-8">Thank You</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light text-white mt-4 leading-tight">
              Your {info?.frequency === "monthly" ? "monthly " : ""}gift{info?.amount ? ` of $${info.amount}` : ""} is a new beginning.
            </h1>
            <p className="text-white/70 font-light mt-6 leading-relaxed">
              A receipt is on its way to your email. Because of you, another mother, child, or young person
              takes a step toward safety, stability, and hope. Thank you for standing beside them.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/foundation" className="overline px-6 py-4 bg-white text-black hover:bg-white/85 transition-colors">Back to the Foundation</Link>
              <Link to="/foundation/new-beginnings" className="overline px-6 py-4 border border-white/25 text-white hover:bg-white hover:text-black transition-colors">New Beginnings</Link>
            </div>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="pay-pending">
            <Heart size={44} className="mx-auto text-white" />
            <p className="font-serif text-3xl font-light text-white mt-8">Your donation is processing.</p>
            <p className="text-white/60 mt-4">Thank you! Your payment is being finalized. You'll receive an email receipt shortly.</p>
            <Link to="/foundation" className="inline-block mt-10 overline px-6 py-4 bg-white text-black hover:bg-white/85 transition-colors">Back to the Foundation</Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="pay-error">
            <p className="font-serif text-3xl font-light text-white mt-8">We couldn't confirm this donation.</p>
            <p className="text-white/60 mt-4">If you were charged, a receipt will still arrive by email. Otherwise, please try again.</p>
            <Link to="/foundation/donate" className="inline-block mt-10 overline px-6 py-4 bg-white text-black hover:bg-white/85 transition-colors">Return to Donate</Link>
          </div>
        )}
      </div>
    </div>
  );
}
